<template>
    <div class="card">
        <div class="card-body">
            <form @submit.prevent="attemptSave" ref="accountForm" novalidate>

                <div class="row">
                    <div class="col-sm-9">
                        <div class="form-group row">
                            <label for="nameInput" class="col-sm-2 col-form-label">Your Name</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <input type="text" id="nameInput" name="name" v-validate="validation.name" v-model="name"
                                           placeholder="Your Name" class="form-control" required>
                                    <div class="invalid-feedback" v-if="errors.has('name')">{{ errors.first('name') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label for="businessNameInput" class="col-sm-2 col-form-label">Business Name</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <input type="text" id="businessNameInput" name="business_name" v-validate="validation.business_name" v-model="business_name"
                                           placeholder="Business Name" class="form-control" :required="hasBusinessName" :readonly="!hasBusinessName">
                                    <div class="invalid-feedback" v-if="errors.has('business_name')">{{ errors.first('business_name') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label for="emailInput" class="col-sm-2 col-form-label">Email Address</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <input type="email" id="emailInput" name="email" v-validate="validation.email" v-model="email"
                                           placeholder="Email Address" class="form-control">
                                    <div class="invalid-feedback" v-if="errors.has('email')">{{ errors.first('email') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row">
                            <label for="emailInput" class="col-sm-2 col-form-label">Password</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <input type="password" id="passwordInput" name="password" v-model="password"
                                           placeholder="Password" class="form-control">
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row" v-if="['contractor', 'grower'].includes(currentUser.role.name)">
                            <label class="col-sm-2 col-form-label">Region</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <VueSelect
                                            v-validate="validation.region"
                                            data-vv-name="region"
                                            v-model="region"
                                            :options="allCountries"
                                            :allow-empty="false"
                                            deselect-label=""
                                            placeholder="Region"
                                            group-values="regions"
                                            group-label="name"
                                            label="name"
                                            track-by="id"
                                    >
                                    </VueSelect>
                                    <div class="invalid-feedback" v-if="errors.has('region')">{{ errors.first('region') }}</div>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row" v-if="['contractor', 'grower'].includes(currentUser.role.name)">
                            <label for="nzgapInput" class="col-sm-2 col-form-label">NZGap Number</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <input type="text" id="nzgapInput" name="nzgap" v-model="nzgap"
                                           placeholder="NZGap Number" class="form-control">
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>

                        <div class="form-group row" v-if="['grower'].includes(currentUser.role.name)">
                            <label for="physicalAddressInput" class="col-sm-2 col-form-label">Physical Address</label>
                            <div class="col-sm-10">
                                <div class="position-relative">
                                    <textarea id="physicalAddressInput" name="physical_address" v-model="physical_address"
                                              placeholder="Physical Address" class="form-control" v-autosize></textarea>
                                    <i class="form-group__bar"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-sm-3 text-right">
                        <div class="position-relative">
                            <Avatar :rounded="false" :src="currentUser.avatar" backgroundColor="#61c365" :username="currentUser.name" />
                            <a href="#" @click.prevent="enableChangingAvatar" class="zmdi zmdi-camera profile_img_overlay"></a>
                        </div>
                    </div>
                </div>

                <div class="text-right">
                    <button type="submit" class="btn btn-success">Save</button>
                </div>
            </form>
        </div>

        <b-modal id="avatarModal" size="lg" ref="modal" title="Change Avatar" body-class="px-0 pb-0" :lazy="true"
                 :no-close-on-backdrop="true" v-model="changingAvatar" :hide-header-close="true">

            <div class="modal-body pt-0">
                <b-form-file v-model="newAvatar" accept="image/*" placeholder="Choose an image..." />
            </div>

            <div v-if="newAvatarSrc">
                <div class="text-center">
                    <p>Select the region you would like to use as your profile picture. Pinch/Scroll to zoom image.</p>
                </div>
                <VueCropper ref='cropper' :view-mode="2" :src="newAvatarSrc" drag-mode="move"
                            :min-crop-box-width="100" :min-crop-box-height="100" :containerStyle="{'max-height': '70vh'}"
                            :aspect-ratio="1" v-if="newAvatarSrc" />

                <b-progress :value="uploadingAvatarProgress" :label="`${Math.floor(uploadingAvatarProgress)}%`" animated
                            v-if="uploadingAvatar" />
            </div>

            <template slot="modal-footer">
                <button class="btn btn-secondary mr-auto" :disabled="uploadingAvatar" @click="handleRemoveAvatar">
                    Remove
                </button>
                <button class="btn btn-secondary" :disabled="uploadingAvatar" @click="disableChangingAvatar">
                    Cancel
                </button>
                <button class="btn btn-primary" :disabled="!Boolean(newAvatar) || uploadingAvatar" @click="handleChangeAvatar">
                    Save
                </button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import Avatar from '@components/ResponsiveAvatar';
import updateAvatarMutation from '@views/Setup/AccountManager/mutationUpdateAvatar.graphql';
import updateUserMutation from '@views/Setup/AccountManager/mutationUpdateUser.graphql';
import listCountriesQuery from '@views/Setup/AccountManager/queryListCountries.graphql';


export default {
  name: 'AccountManager',
  props: ['currentUser'],
  apollo: {
    allCountries: {
      query: listCountriesQuery,
      update: data => data.filterCountries.data,
    },
  },
  data() {
    return {
      loading: 0,
      name: this.currentUser.name,
      business_name: this.currentUser.business_name,
      email: this.currentUser.email,
      password: null,
      region: this.currentUser.region,
      nzgap: this.currentUser.nzgap,
      physical_address: this.currentUser.physical_address,
      allCountries: [],
      newAvatar: null,
      newAvatarSrc: null,
      changingAvatar: false,
      uploadingAvatar: false,
      uploadingAvatarProgress: 0,
      avatar: null,
      validation: {
        name: { required: true },
        business_name: { required: true },
        region: { required: true },
        email: { required: true, email: true },
      },
    };
  },
  methods: {
    attemptSave() {
      this.errors.remove('global');

      this.$validator.validateAll().then(validationWasSuccessful => {
        // this.$refs.accountForm.classList.add('was-validated');
        if (!validationWasSuccessful) {
          return;
        }

        const input = {
          id: this.currentUser.id,
          name: this.name,
          email: this.email,
          password: this.password,
          region_id: this.region ? this.region.id : null,
          nzgap: this.nzgap,
          physical_address: this.physical_address,
        };

        if(this.hasBusinessName) {
          input.business_name = this.business_name;
        }

        this.$apollo
          .mutate({
            mutation: updateUserMutation,
            variables: {input},
            optimisticResponse: {
              __typename: 'Mutation',
              updateUser: {
                __typename: 'User',
                ...input
              },
            },
          })
          .then(() => {
            this.$notifySuccess('Profile saved!');
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure('Unfortunately, we were unable to do that!');
          });
      });
    },
    enableChangingAvatar() {
      this.changingAvatar = true;
    },

    disableChangingAvatar() {
      this.disableUploadingAvatar();
      this.changingAvatar = false;
    },

    enableUploadingAvatar() {
      this.uploadingAvatar = true;
      this.uploadingAvatarProgress = 0;
    },

    disableUploadingAvatar() {
      this.newAvatar = null;
      this.uploadingAvatar = false;
      this.uploadingAvatarProgress = 0;
    },

    handleRemoveAvatar() {
      this.$apollo
        .mutate({
          mutation: updateAvatarMutation,
          variables: {
            input: { id: this.currentUser.id, avatar_id: null },
          },
        })
        .then(() => {
          this.$notifySuccess('Profile picture updated');
          this.disableChangingAvatar();
        });
    },

    handleChangeAvatar() {
      this.enableUploadingAvatar();
      const data = this.$refs.cropper.getData();
      const cropData = {
        x: Math.round(parseFloat(data.x)),
        y: Math.round(parseFloat(data.y)),
        crop_width: Math.round(parseFloat(data.width)),
        crop_height: Math.round(parseFloat(data.height)),
        output_width: 500,
        output_height: 500,
      };

      const onProgress = e => {
        const progress = Math.round(e.loaded * 100 / e.total);
        this.uploadingAvatarProgress = progress > 99 ? 99 : progress;
      };

      this.$uploadSingleFile(this.newAvatar, cropData, onProgress)
        .then(({ data: { file_id } }) => {
          this.$apollo
            .mutate({
              mutation: updateAvatarMutation,
              variables: {
                input: {
                  id: this.currentUser.id,
                  avatar_id: file_id,
                },
              },
            })
            .then(() => {
              this.$notifySuccess('Profile picture updated');
              this.disableChangingAvatar();
              this.disableUploadingAvatar();
            });
        })
        .catch(err => {
          console.error('failed', err);
          this.$notifyFailure(
            'Unfortunately, we were unable to do that!',
          );
        });
    },
  },
  computed: {
    hasBusinessName() {
      return !['processor', 'association'].includes(this.currentUser.role.name);
    }
  },
  watch: {
    newAvatar(file) {
      this.newAvatarSrc = null;
      if (file) {
        const oFReader = new FileReader();
        oFReader.onload = ({ target: { result } }) =>
          (this.newAvatarSrc = result);
        oFReader.readAsDataURL(file);
      }
    },
  },
  components: {
    Avatar,
  },
};
</script>
